(() => {
  const GENERIC_COLLECTIONS = new Set(['all', 'all-products', 'frontpage']);
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';

  const toTitle = (handle) =>
    handle
      .split('-')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const parseXmlUrls = (xmlText, pattern) => {
    const doc = new DOMParser().parseFromString(xmlText, 'application/xml');

    return Array.from(doc.querySelectorAll('loc'))
      .map((node) => node.textContent)
      .filter((url) => url && pattern.test(url));
  };

  const fetchText = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin' });

    if (!response.ok) {
      throw new Error(`Unable to fetch ${url}`);
    }

    return response.text();
  };

  const getCollectionSitemapUrls = async () => {
    const sitemap = await fetchText('/sitemap.xml');
    return parseXmlUrls(sitemap, /sitemap_collections/i).map((url) => new URL(url).pathname + new URL(url).search);
  };

  const getCollectionUrls = async () => {
    const sitemapUrls = await getCollectionSitemapUrls();
    const collectionUrlGroups = await Promise.all(
      sitemapUrls.map(async (sitemapUrl) => parseXmlUrls(await fetchText(sitemapUrl), /\/collections\//i))
    );

    return collectionUrlGroups
      .flat()
      .map((url) => new URL(url))
      .filter((url) => !GENERIC_COLLECTIONS.has(url.pathname.split('/').filter(Boolean).pop()))
      .map((url) => url.pathname);
  };

  const collectionIncludesProduct = (doc, productHandle) =>
    Array.from(doc.querySelectorAll(PRODUCT_LINK_SELECTOR)).some((link) => {
      const href = link.getAttribute('href') || '';
      return href.includes(`/products/${productHandle}`);
    });

  const findProductCollections = async (productHandle) => {
    const collectionUrls = await getCollectionUrls();
    const matches = [];
    let cursor = 0;

    const worker = async () => {
      while (cursor < collectionUrls.length) {
        const collectionUrl = collectionUrls[cursor];
        cursor += 1;

        try {
          const html = await fetchText(collectionUrl);
          const doc = new DOMParser().parseFromString(html, 'text/html');

          if (collectionIncludesProduct(doc, productHandle)) {
            const handle = collectionUrl.split('/').filter(Boolean).pop();
            const title = doc.querySelector('.collection-hero__title, h1')?.textContent?.trim() || toTitle(handle);
            matches.push({ title, url: collectionUrl });
          }
        } catch (error) {
          // Collection lookup is progressive enhancement; leave the server-rendered page intact on failures.
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(6, collectionUrls.length) }, worker));
    return matches;
  };

  const renderLinks = (nav, collections) => {
    if (!collections.length) return;

    const heading = nav.dataset.heading;
    const list = document.createElement('div');

    nav.replaceChildren();
    list.className = 'product__collection-links-list';

    if (heading) {
      const headingElement = document.createElement('span');
      headingElement.className = 'product__collection-links-heading';
      headingElement.textContent = heading;
      nav.append(headingElement);
    }

    collections.forEach((collection) => {
      const link = document.createElement('a');
      link.className = 'product__collection-link';
      link.href = collection.url;
      link.textContent = collection.title;
      list.append(link);
    });

    nav.append(list);
    nav.hidden = false;
  };

  const initFallback = async (nav) => {
    const productHandle = nav.dataset.productHandle;
    if (!productHandle) return;

    const cacheKey = `theme:product-collections:${productHandle}`;
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      renderLinks(nav, JSON.parse(cached));
      return;
    }

    const collections = await findProductCollections(productHandle);
    sessionStorage.setItem(cacheKey, JSON.stringify(collections));
    renderLinks(nav, collections);
  };

  document.querySelectorAll('[data-product-collection-fallback]').forEach((nav) => {
    initFallback(nav);
  });
})();
